import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Check, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const DOCUMENT_CATEGORIES = [
  'Financial',
  'Legal',
  'Operational',
  'HR',
  'IT',
  'Insurance',
  'Facilities',
  'Sales',
  'Procurement',
  'General',
];

export default function DealPermissions({ deal, currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // userId being saved
  const [error, setError] = useState('');

  const isAdmin = currentUser?.role === 'admin' || currentUser?.isAdmin;

  useEffect(() => {
    if (deal?.id) fetchPermissions();
  }, [deal?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/deals/${deal.id}/member-permissions`, {
        withCredentials: true,
      });
      setUsers(res.data.users || []);
    } catch (err) {
      setError('Failed to load permissions.');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = async (userId, category) => {
    if (!isAdmin) return;
    const user = users.find(u => u.id === userId);
    if (!user) return;

    const blocked = user.blockedCategories || [];
    const newBlocked = blocked.includes(category)
      ? blocked.filter(c => c !== category)
      : [...blocked, category];

    // Optimistic update
    setUsers(prev =>
      prev.map(u => u.id === userId ? { ...u, blockedCategories: newBlocked } : u)
    );

    setSaving(userId);
    try {
      await axios.put(
        `${API_URL}/api/deals/${deal.id}/member-permissions/${userId}`,
        { blockedCategories: newBlocked },
        { withCredentials: true }
      );
    } catch (err) {
      setError('Failed to save permissions.');
      // Revert
      setUsers(prev =>
        prev.map(u => u.id === userId ? { ...u, blockedCategories: blocked } : u)
      );
    } finally {
      setSaving(null);
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: 24, color: '#6B7280', textAlign: 'center' }}>
        <Shield size={32} style={{ marginBottom: 8 }} />
        <p>Deal Permissions are only visible to admins.</p>
      </div>
    );
  }

  if (loading) {
    return <div style={{ padding: '24px 24px 32px', color: '#6B7280' }}>Loading permissions...</div>;
  }

  if (error) {
    return <div style={{ padding: '24px 24px 32px', color: '#EF4444' }}>{error}</div>;
  }

  return (
    <div style={{ padding: '24px 24px 32px' }}>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 4 }}>
          Document Access Permissions
        </h3>
        <p style={{ fontSize: 13, color: '#6B7280' }}>
          Checked categories are <strong>visible</strong> to each member. Uncheck to block access.
          Admins always see all documents.
        </p>
      </div>

      {users.length === 0 && (
        <p style={{ color: '#9CA3AF', fontSize: 13 }}>No team members found for this organization.</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {users.map(user => {
          const blocked = user.blockedCategories || [];
          const isSavingThis = saving === user.id;

          return (
            <div
              key={user.id}
              style={{
                border: '1px solid #E5E7EB',
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              {/* User header */}
              <div
                style={{
                  padding: '10px 14px',
                  background: '#F9FAFB',
                  borderBottom: '1px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
                    {user.name}
                  </span>
                  <span style={{ fontSize: 12, color: '#6B7280', marginLeft: 8 }}>
                    {user.email}
                  </span>
                  {user.role === 'admin' && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        background: '#EFF6FF',
                        color: '#2563EB',
                        padding: '2px 6px',
                        borderRadius: 4,
                      }}
                    >
                      Admin
                    </span>
                  )}
                </div>
                {isSavingThis && (
                  <span style={{ fontSize: 12, color: '#6B7280' }}>Saving...</span>
                )}
                {blocked.length > 0 && (
                  <span
                    style={{
                      fontSize: 12,
                      background: '#FEF2F2',
                      color: '#DC2626',
                      padding: '2px 8px',
                      borderRadius: 4,
                    }}
                  >
                    {blocked.length} blocked
                  </span>
                )}
              </div>

              {/* Category grid */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: 0,
                }}
              >
                {DOCUMENT_CATEGORIES.map(cat => {
                  const isBlocked = blocked.includes(cat);
                  const isAllowed = !isBlocked;
                  return (
                    <button
                      key={cat}
                      onClick={() => toggleCategory(user.id, cat)}
                      disabled={user.role === 'admin'}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 12px',
                        background: isAllowed ? '#fff' : '#FEF2F2',
                        border: 'none',
                        borderRight: '1px solid #F3F4F6',
                        borderBottom: '1px solid #F3F4F6',
                        cursor: user.role === 'admin' ? 'default' : 'pointer',
                        fontSize: 13,
                        color: isAllowed ? '#111827' : '#9CA3AF',
                        textAlign: 'left',
                        opacity: user.role === 'admin' ? 0.5 : 1,
                      }}
                    >
                      <span
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 4,
                          border: `2px solid ${isAllowed ? '#10B981' : '#D1D5DB'}`,
                          background: isAllowed ? '#10B981' : '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {isAllowed ? <Check size={10} color="#fff" /> : <X size={10} color="#D1D5DB" />}
                      </span>
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
